import { set, child, update, remove, onValue } from 'firebase/database'
import { mapKeys } from 'lodash-es'
import { proxy, useSnapshot } from 'valtio'

import * as firebaseConnection from '../firebaseConnection'
import {
  type PlaybackMetrics,
  type PlaybackSettings,
  type PlaybackState,
} from '../model/playback-model'

const ROOT_NODES = {
  state: 'videoPlaybacks',
  settings: 'videoPlaybackSettings',
  metrics: 'videoPlaybackMetrics',
}

const playbackState = proxy<{
  state: PlaybackState | null
  settings: PlaybackSettings | null
  metrics: PlaybackMetrics | null
  isStateReady: boolean
  isMetricsReady: boolean
}>({
  state: null,
  settings: null,
  metrics: null,
  isStateReady: false,
  isMetricsReady: false,
})

// Subscribe
firebaseConnection.runWhenReady((connection) => {
  // Adds a Firebase listener to track video playback settings updates.
  onValue(child(connection.room, ROOT_NODES.settings), (snapshot) => {
    playbackState.settings = snapshot.val()
  })

  // Adds a Firebase listener to track video playback state updates.
  onValue(child(connection.room, ROOT_NODES.state), (snapshot) => {
    playbackState.state = snapshot.val()
    playbackState.isStateReady = true
  })

  // Adds a Firebase listener to track video playback metrics updates.
  onValue(child(connection.room, ROOT_NODES.metrics), (snapshot) => {
    playbackState.metrics = snapshot.val()
    playbackState.isMetricsReady = true
  })
})

// Actions

/**
 * Sets the playback state.
 * @param state - The playback state.
 * @param payload - The payload associated with the playback state.
 */
const setPlaybackState = async (
  state: string,
  payload?: Partial<PlaybackState['statePayload']>
) => {
  const connection = await firebaseConnection.get()

  return update(child(connection.room, ROOT_NODES.state), {
    state,
    ...mapKeys(payload, (_, key) => `statePayload/${key}`),
  }).catch((err: Error) => {
    connection.logError(
      'SET_VIDEO_PLAYBACK_STATE',
      'Failed to execute the last video playback command',
      err
    )
  })
}

/**
 * Removes the video playback state.
 * @returns A promise that resolves when the video playback state is removed.
 */
const removePlaybackState = async () => {
  const connection = await firebaseConnection.get()

  return remove(child(connection.room, ROOT_NODES.state)).catch(
    (err: Error) => {
      connection.logError(
        'REMOVE_VIDEO_PLAYBACK_STATE',
        'Failed to stop video playback',
        err
      )
    }
  )
}

/**
 * Sets the video playback settings.
 * @param settings - The video playback settings.
 * @returns A promise that resolves when the video playback settings are set.
 */
const setPlaybackSettings = async (settings: PlaybackSettings) => {
  const connection = await firebaseConnection.get()

  return set(child(connection.room, ROOT_NODES.settings), settings).catch(
    (err: Error) => {
      connection.logError(
        'SET_VIDEO_PLAYBACK_SETTINGS',
        'Failed to set video playing progress',
        err
      )
    }
  )
}

/**
 * Sets the video playback metrics.
 * @param metrics - The video playback metrics.
 * @returns A promise that resolves when the video playback metrics are set.
 */
const setPlaybackMetrics = async (metrics: PlaybackMetrics) => {
  const connection = await firebaseConnection.get()

  return set(child(connection.room, ROOT_NODES.metrics), metrics).catch(
    (err: Error) => {
      connection.logError(
        'SET_VIDEO_PLAYBACK_METRICS',
        'Failed to set synchronise video playing progress',
        err
      )
    }
  )
}

/**
 * Updates the video playback metrics.
 * @param key - The key of the video playback metrics.
 * @param value - The value of the video playback metrics.
 * @returns A promise that resolves when the video playback metrics are updated.
 */
const updatePlaybackMetrics = async <K extends keyof PlaybackMetrics>(
  key: K,
  value: PlaybackMetrics[K]
) => {
  const connection = await firebaseConnection.get()

  return set(
    child(connection.room, `videoPlaybackMetrics/${key}`),
    value
  ).catch((err: Error) => {
    connection.logError(
      'SET_VIDEO_PLAYBACK_METRICS',
      'Failed to synchronise video playing progress',
      err
    )
  })
}

const usePlaybackSettings = () => useSnapshot(playbackState).settings
const usePlaybackState = () => useSnapshot(playbackState).state
const usePlaybackMetrics = () => useSnapshot(playbackState).metrics
const useIsPlaybackStateReady = () => useSnapshot(playbackState).isStateReady
const useIssetPlaybackMetricsReady = () =>
  useSnapshot(playbackState).isMetricsReady

const usePlaybackVideoId = () =>
  useSnapshot(playbackState).state?.statePayload?.photoId

export {
  playbackState,
  usePlaybackSettings,
  usePlaybackState,
  usePlaybackMetrics,
  setPlaybackState,
  removePlaybackState,
  setPlaybackSettings,
  setPlaybackMetrics,
  updatePlaybackMetrics,
  useIsPlaybackStateReady,
  useIssetPlaybackMetricsReady,
  usePlaybackVideoId,
}
